import React, { useContext, useState } from 'react';
import {
  DialogBox,
  AgGridTableWrapper,
  AgGridWidgetWrapper,
  Watermark,
  Loader,
} from '@mummssoftware/common-ui';
import { YYYYMMDDtoMMDDYY } from '@mummssoftware/utils/formatters';
import {
  getApprovalMilestone,
  hopeDocumentProps,
} from '@mummssoftware/utils/hope';
import { isUserImpersonating } from '@mummssoftware/utils/auth';
import { HopeGlobalContext } from '@mummssoftware/global-context';
import { IconButton } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { GridOptions } from 'ag-grid-community';
import { useTheme } from '@material-ui/core/styles';
import { AgGridReact } from 'ag-grid-react';
import { HopeContext } from '../Context/HopeWidgetContext';
import { getColumnDefs, commonColumnDefs } from '../HopeTable/tableColumnDefs';
import { SET_HOPE_DOC_DATA, SET_SHOW_LOADER } from '../Context/constants';
import {
  StyledButton,
  DetailModalButtonWrapper,
  BackButtonWrapper,
} from '../../components/layout/WrapperDiv';
import { Context } from '../../context';
import HopeReviewDocument from '../HopeReviewDocument';
import { ConfirmApproval } from './ConfirmApproval';
import defaults from '../../translations/en.json';

export const DetailModal = React.memo(() => {
  const {
    patientHopeDocData,
    dispatch,
    showLoader,
    clearHopeData,
    showDetailRow,
    getPatientHopeDetails,
    updateHopeWidgetStatus,
  } = useContext(HopeContext);
  const { watermark, smallWidget, currentUser, patient } = useContext(Context);
  const { patientHopeDetail } = useContext(HopeGlobalContext);
  // get last row in details to check milestones for approval
  const approvalMilestone =
    (!!showDetailRow && getApprovalMilestone(showDetailRow)) || '';
  const hopeDTOProp = hopeDocumentProps[approvalMilestone] || '';
  const {
    props: { grid: gridTheme },
    palette,
    palette: {
      background: { main, oddrow },
    },
  } = useTheme() as any;
  const rowData = showDetailRow;
  const hopeDocHasErrors =
    patientHopeDocData &&
    !patientHopeDocData.every(
      (row: any) => row[hopeDTOProp] === 'true' && row.errorlevel < 2,
    );

  const [showConfirm, setShowConfirm] = useState(false);

  const gridOptions: GridOptions = {
    detailRowAutoHeight: true,
    embedFullWidthRows: true,
    groupHeaderHeight: 20,
    defaultColDef: {
      ...commonColumnDefs,
    },
    // getColumnDefs(palette, batchMode?, patient?) returns a tuple
    // [detailColumnDefs, summaryColumnDefs, smallSummaryColumnDefs];
    // here batchMode is omitted (undefined) and we pick [0] = detail defs.
    columnDefs: getColumnDefs(palette, undefined, patient)[0],
  };

  const title =
    (rowData &&
      `${rowData.firstname} ${rowData.lastname} - ${rowData.patientnumber} - ${
        defaults.AdmitDate
      }: ${YYYYMMDDtoMMDDYY(rowData.admissiondate_shifted)}`) ||
    '';

  const handleClose = () => {
    clearHopeData({ refreshSummary: true });
  };
  const handleBack = () => {
    dispatch({ type: SET_HOPE_DOC_DATA, payload: null });
  };

  const approveDoc = () => {
    dispatch({ type: SET_SHOW_LOADER, payload: true });
    return updateHopeWidgetStatus(
      rowData?.patientId,
      rowData?.episodenum,
      approvalMilestone,
      'approve',
    ).then(() => {
      getPatientHopeDetails(rowData?.patientId, rowData?.episodenum);
      dispatch({
        type: SET_HOPE_DOC_DATA,
        payload: null,
      });
    });
  };

  const approveButtonOnClick = () => {
    if (!isUserImpersonating(currentUser) && hopeDocHasErrors) {
      setShowConfirm(true);
    } else {
      approveDoc();
    }
  };

  const clearConfirmation = () => {
    setShowConfirm(false);
  };

  // for 'grouping' rows where multiple events happen on the same day by background color.
  const getRowDayBackground = (props: any) => {
    const {
      data: { dayofservice },
    } = props;
    return {
      backgroundColor: Number.parseInt(dayofservice) % 2 === 0 ? main : oddrow,
    };
  };

  return (
    <>
      <DialogBox
        open={!!showDetailRow || !!patientHopeDocData}
        handleClick={handleClose}
        fullWidth
        disableScrollLock
        maxWidth="lg"
        title={title}
        contentStyle={{ padding: '0px' }}
        TopButton={
          <DetailModalButtonWrapper>
            {patientHopeDocData && patientHopeDetail && (
              <BackButtonWrapper>
                <IconButton
                  title={defaults.goBack}
                  size="small"
                  color="primary"
                  onClick={handleBack}
                >
                  <ArrowBackIcon />
                </IconButton>
              </BackButtonWrapper>
            )}
            {patientHopeDocData && (
              <StyledButton
                variant="contained"
                style={{
                  backgroundColor: palette.primary.main,
                  color: palette.text.contrastPrimaryPaper,
                }}
                onClick={approveButtonOnClick}
                disabled={showLoader}
              >
                {defaults.Approve}
              </StyledButton>
            )}
          </DetailModalButtonWrapper>
        }
      >
        <div style={{ minHeight: '30vh' }}>
          {showLoader && <Loader />}
          {watermark && watermark.show ? (
            <Watermark
              readOnly={watermark.readOnly}
              smallWidget={smallWidget}
            />
          ) : null}

          <ConfirmApproval
            onClickCancel={clearConfirmation}
            showConfirm={showConfirm}
          />

          {patientHopeDocData && approvalMilestone && (
            <HopeReviewDocument smallWidget={false} hopeDTOProp={hopeDTOProp} />
          )}
          {!patientHopeDocData && (
            <AgGridWidgetWrapper
              id="health-info-body"
              smallWidget={false}
              heightOffset={100}
            >
              <AgGridTableWrapper
                id="hope-table-wrapper"
                className={`${gridTheme.className} light max-width`}
                printMode={false}
              >
                <AgGridReact
                  getRowStyle={getRowDayBackground}
                  gridOptions={gridOptions}
                  rowData={
                    (patientHopeDetail as mumms.HopeEvent[]) || undefined
                  }
                  rowHeight={gridTheme.rowHeight}
                  headerHeight={gridTheme.headerHeight}
                  suppressRowClickSelection
                  suppressCellSelection
                />
              </AgGridTableWrapper>
            </AgGridWidgetWrapper>
          )}
        </div>
      </DialogBox>
    </>
  );
});
